import React from "react";
import { ConfidencePill, StatusPill } from "../Pills";
import { AutomationBadge } from "../ConnectorSummary";
import { Database, RefreshCw, Settings as Cog } from "lucide-react";
import { relativeTime } from "../../lib/format";

/**
 * Single configured data source row. Pure presentational; the parent owns
 * sync state and configure modal state.
 */
function DataSourceRow({
  source: s, providerLabels, connectorLabels, domainColor,
  syncingId, onSync, onConfigure,
}) {
  const isSyncing = syncingId === s.id;
  return (
    <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`ds-row-${s.id}`}>
      <div className="flex items-start gap-3 min-w-0">
        <div
          className="w-9 h-9 rounded-md grid place-items-center flex-shrink-0"
          style={{ background: `${domainColor[s.domain] || "#27272A"}20`, color: domainColor[s.domain] }}
        >
          <Database className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <div className="font-medium text-sm truncate">{providerLabels[s.provider] || s.provider}</div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.domain}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">{s.notes || "—"}</div>
          <div className="flex items-center flex-wrap gap-2 mt-2">
            <StatusPill status={s.status} />
            <ConfidencePill level={s.confidence_level} />
            <AutomationBadge mode={s.connector_mode} testid={`automation-badge-${s.id}`} />
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{connectorLabels[s.connector_mode]}</span>
            <span className="text-[10px] text-muted-foreground">· last sync {relativeTime(s.last_sync_at)}</span>
          </div>
        </div>
      </div>
      <div className="flex gap-2 self-start sm:self-center">
        {(s.provider === "open_meteo" || (s.provider === "wunderground" && s.status === "connected")) && (
          <button
            onClick={() => onSync(s)}
            disabled={isSyncing}
            className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border hover:bg-secondary disabled:opacity-50"
            data-testid={`ds-sync-${s.id}`}
          >
            <RefreshCw className={`w-3 h-3 ${isSyncing ? "animate-spin" : ""}`} />
            {isSyncing ? "Syncing…" : "Sync now"}
          </button>
        )}
        <button
          onClick={() => onConfigure(s)}
          className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border hover:bg-secondary"
          data-testid={`ds-configure-${s.id}`}
        >
          <Cog className="w-3 h-3" /> Configure
        </button>
      </div>
    </div>
  );
}

export default function DataSourceList(props) {
  const { sources, loading } = props;
  return (
    <section className="card-base overflow-hidden">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <div className="label-tiny">{sources.length} configured · sorted by domain</div>
      </div>
      <div className="divide-y divide-border">
        {sources.map((s) => (
          <DataSourceRow key={s.id} source={s} {...props} />
        ))}
        {!loading && sources.length === 0 && (
          <div className="px-5 py-10 text-center text-muted-foreground text-sm">No data sources yet.</div>
        )}
      </div>
    </section>
  );
}
